import type {
  AnchorFormatting,
  MarkedDocument,
} from "@app/components/editor/document/DocumentCommentAnchor";
import {
  anchorsToMarks,
  getMarkedCommentIds,
  marksToAnchors,
  substituteAnchorDirectives,
} from "@app/components/editor/document/DocumentCommentAnchor";
import {
  COMMENT_MARK_NAME,
  withoutOrphanCommentMarks,
} from "@app/components/editor/document/DocumentComments";
import { DOCUMENT_FRAME_NODE_NAME } from "@app/components/editor/document/DocumentFrame";
import { DOCUMENT_IMAGE_NODE_NAME } from "@app/components/editor/document/DocumentImage";
import {
  isWritableTask,
  LIST_ITEM_NODE_NAME,
  taskItemTokens,
  withSeparatedLists,
} from "@app/components/editor/document/DocumentListItem";
import {
  hasExtraCells,
  TIPTAP_DEFAULT_PRIORITY,
  withColumnAlignments,
} from "@app/components/editor/document/DocumentTable";
import { documentExtensions } from "@app/components/editor/document/extensions";
import { createDocumentMarked } from "@app/components/editor/document/marked_instance";
import {
  parseFrameEmbedHeight,
  parseFrameEmbedProps,
  serializeFrameEmbedDirective,
} from "@app/lib/markdown/frame_embed";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { isString } from "@app/types/shared/utils/general";
import type {
  ExtendableConfig,
  JSONContent,
  MarkdownToken,
} from "@tiptap/core";
import { flattenExtensions, getExtensionField, getSchema } from "@tiptap/core";
import { MarkdownManager } from "@tiptap/markdown";
import type { Mark, Node } from "@tiptap/pm/model";
import { Fragment } from "@tiptap/pm/model";
import { renderToHTMLString } from "@tiptap/static-renderer/pm/html-string";
import isEqual from "lodash/isEqual";

export const documentSchema = getSchema(documentExtensions);
const documentMarkdown = new MarkdownManager({
  // Markdown reads a mark written inside a code span as text: code ranks back inside the others.
  // TODO(louisjc): drop once TipTap writes code marks innermost whatever their priority:
  // https://github.com/ueberdosis/tiptap/issues/8568
  extensions: documentExtensions.map((extension) =>
    extension.name === "code"
      ? extension.extend({ priority: TIPTAP_DEFAULT_PRIORITY })
      : extension
  ),
  marked: createDocumentMarked(),
});

type MarkdownLexer = InstanceType<typeof documentMarkdown.instance.Lexer>;
type MarkdownTokens = ReturnType<MarkdownLexer["lex"]>;

interface TokenParser {
  activeParseLexer: MarkdownLexer | null;
  parseTokens(
    tokens: MarkdownTokens,
    parseImplicitEmptyParagraphs: boolean
  ): JSONContent[];
}

const isTokenParser = (manager: object): manager is TokenParser =>
  "activeParseLexer" in manager &&
  "parseTokens" in manager &&
  typeof manager.parseTokens === "function";

// MarkdownManager.parse lexes the Markdown, then parses the tokens with that lexer active, a
// step it keeps private. Doing both steps here parses the tokens the support check walked
// instead of lexing every document twice; a TipTap without that step parses through `parse`.
const markdownManager: object = documentMarkdown;
const tokenParser = isTokenParser(markdownManager) ? markdownManager : null;

interface LexedMarkdown {
  markdown: string;
  lexer: MarkdownLexer;
  tokens: MarkdownTokens;
}

const lexMarkdown = (markdown: string): LexedMarkdown => {
  const lexer = new documentMarkdown.instance.Lexer(
    documentMarkdown.instance.defaults
  );
  return { markdown, lexer, tokens: lexer.lex(markdown) };
};

/**
 * @cc [owner:flvndvd,label:architecture] document-markdown-capabilities
 * Supported token names MUST derive from the editor extensions' parse and render handlers, plus
 * `TEXT_ONLY_TOKENS`, which carry only text and need no extension. A task item's `checkbox`
 * token is structure its list item handler consumes: it MUST NOT be checked as a token of its
 * own there, and MUST be unsupported anywhere else.
 */
const supportedMarkdownTokens = new Set(
  flattenExtensions(documentExtensions)
    .filter(
      (extension) =>
        getExtensionField<ExtendableConfig["parseMarkdown"]>(
          extension,
          "parseMarkdown"
        ) &&
        getExtensionField<ExtendableConfig["renderMarkdown"]>(
          extension,
          "renderMarkdown"
        )
    )
    .map(
      (extension) =>
        getExtensionField<ExtendableConfig["markdownTokenName"]>(
          extension,
          "markdownTokenName"
        ) || extension.name
    )
);

// Whitespace separates blocks, and an escape (`\*`, `\_`, `\~`) is a character the serializer
// escapes in plain text: neither has an editor extension.
const TEXT_ONLY_TOKENS = new Set(["space", "escape"]);

const isSupportedMarkdownToken = (token: MarkdownToken) =>
  token.type !== undefined &&
  (TEXT_ONLY_TOKENS.has(token.type) ||
    supportedMarkdownTokens.has(token.type)) &&
  !(token.type === "table" && hasExtraCells(token));

/** A Markdown element the editor cannot keep and can name. */
export type UnsupportedElement =
  | "table_extra_cells"
  | "html"
  | "reference_definition";

const UNSUPPORTED_ELEMENT_NAMES: Record<UnsupportedElement, string> = {
  table_extra_cells: "a table row with more cells than its header",
  html: "HTML",
  reference_definition: "a reference link definition",
};

const unsupportedElement = (
  token: MarkdownToken
): UnsupportedElement | null => {
  switch (token.type) {
    case "table":
      return hasExtraCells(token) ? "table_extra_cells" : null;
    case "html":
      return "html";
    case "def":
      return "reference_definition";
    default:
      return null;
  }
};

/** The tokens marked's `walkTokens` visits under `token`. */
const childTokens = (token: MarkdownToken): MarkdownToken[] => {
  switch (token.type) {
    case "list":
      return token.items ?? [];
    case "list_item":
      return taskItemTokens(token);
    case "table":
      return [...token.header, ...token.rows.flat()].flatMap(
        (cell) => cell.tokens
      );
    default: {
      const fields =
        documentMarkdown.instance.defaults.extensions?.childTokens?.[
          token.type ?? ""
        ];
      return fields
        ? fields.flatMap((field) => token[field]).flat(Infinity)
        : (token.tokens ?? []);
    }
  }
};

// marked's `walkTokens` concatenates its callbacks' results into a new array at every token, which
// makes walking a long document quadratic.
const findToken = (
  tokens: readonly MarkdownToken[],
  matches: (token: MarkdownToken) => boolean
): MarkdownToken | null => {
  for (const token of tokens) {
    const found = matches(token)
      ? token
      : findToken(childTokens(token), matches);
    if (found) {
      return found;
    }
  }
  return null;
};

const listItemSignature = (depth: number, task: boolean, checked: boolean) =>
  `${depth}${task ? (checked ? "x" : "o") : "-"}`;

/** Each list item of the Markdown in document order: its nesting, and whether it is a task, done. */
const tokenListItems = (tokens: readonly MarkdownToken[]): string[] => {
  const items: string[] = [];
  const visit = (token: MarkdownToken, depth: number) => {
    if (token.type === "list_item") {
      items.push(
        listItemSignature(depth, token.task === true, token.checked === true)
      );
    }
    const childDepth = token.type === "list_item" ? depth + 1 : depth;
    childTokens(token).forEach((child) => visit(child, childDepth));
  };
  tokens.forEach((token) => visit(token, 0));
  return items;
};

const documentListItems = (document: JSONContent): string[] => {
  const items: string[] = [];
  const visit = (node: JSONContent, depth: number) => {
    const isItem = node.type === LIST_ITEM_NODE_NAME;
    if (isItem) {
      items.push(
        listItemSignature(
          depth,
          node.attrs?.task === true,
          node.attrs?.checked === true
        )
      );
    }
    node.content?.forEach((child) => visit(child, isItem ? depth + 1 : depth));
  };
  visit(document, 0);
  return items;
};

const hasTaskItem = (tokens: MarkdownTokens) =>
  findToken(tokens, (token) => token.type === "list_item" && token.task) !==
  null;

/**
 * @cc [owner:rfrenoy,label:product] document-task-items-read
 * Markdown with a task item MUST open only when the parsed document has the same list items as
 * marked's GFM tokens, in the same order and nesting, each a task exactly when its token is one
 * and done exactly when its token is checked.
 */
const readsTaskItems = ({ tokens }: LexedMarkdown, document: JSONContent) =>
  !hasTaskItem(tokens) ||
  isEqual(tokenListItems(tokens), documentListItems(document));

const findUnsupportedToken = (tokens: MarkdownTokens): MarkdownToken | null =>
  findToken(tokens, (token) => !isSupportedMarkdownToken(token));

/** The first Markdown element in `content` the editor cannot keep, or null if it has no name. */
export const findUnsupportedElement = (
  content: string
): UnsupportedElement | null => {
  const token = findUnsupportedToken(lexMarkdown(content).tokens);
  return token ? unsupportedElement(token) : null;
};

const hasSupportedMarkdown = ({ tokens }: LexedMarkdown) =>
  findUnsupportedToken(tokens) === null;

const withoutTrailingParagraphs = (document: JSONContent): JSONContent => {
  const content = document.content ?? [];
  let end = content.length;

  while (
    end > 0 &&
    content[end - 1].type === "paragraph" &&
    !content[end - 1].content?.length
  ) {
    end -= 1;
  }

  return { ...document, content: content.slice(0, end) };
};

/** Markdown for an editor document, comment marks written as anchor directives. */
const serializeWithAnchors = (
  document: JSONContent,
  anchorOrder: string[],
  formatting: AnchorFormatting
): Result<string, string> => {
  const { document: anchored, directives } = marksToAnchors(
    document,
    anchorOrder,
    formatting
  );
  return substituteAnchorDirectives(
    documentMarkdown.serialize(withSeparatedLists(anchored)),
    directives
  );
};

// TipTap's paragraph parser returns an image alone in its paragraph without the paragraph, as
// for its block image; this editor's image is inline, so it goes back in one.
const imagesInParagraphs = (node: JSONContent): JSONContent => {
  if (!node.content) {
    return node;
  }
  const holdsInline = documentSchema.nodes[node.type ?? ""]?.inlineContent;
  return {
    ...node,
    content: node.content.map((child) =>
      child.type === DOCUMENT_IMAGE_NODE_NAME && !holdsInline
        ? { type: "paragraph", content: [child] }
        : imagesInParagraphs(child)
    ),
  };
};

// A list item opens with a paragraph, so a frame line opening one reads as the text its paragraph
// writes back escaped.
const framesOpeningListItemsAsText = (node: JSONContent): JSONContent => {
  if (!node.content) {
    return node;
  }
  const content = node.content.map(framesOpeningListItemsAsText);
  const [first] = content;
  if (
    node.type === "listItem" &&
    first?.type === DOCUMENT_FRAME_NODE_NAME &&
    isString(first.attrs?.path)
  ) {
    content[0] = {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: serializeFrameEmbedDirective(
            first.attrs.path,
            parseFrameEmbedHeight(first.attrs.height),
            parseFrameEmbedProps(first.attrs.props)
          ),
        },
      ],
    };
  }
  return { ...node, content };
};

const parseMarkdownTokens = ({
  markdown,
  lexer,
  tokens,
}: LexedMarkdown): JSONContent => {
  if (!tokenParser) {
    return documentMarkdown.parse(markdown);
  }
  const previousLexer = tokenParser.activeParseLexer;
  tokenParser.activeParseLexer = lexer;
  try {
    return { type: "doc", content: tokenParser.parseTokens(tokens, true) };
  } finally {
    tokenParser.activeParseLexer = previousLexer;
  }
};

const parseMarkdown = (markdown: LexedMarkdown): JSONContent =>
  framesOpeningListItemsAsText(
    imagesInParagraphs(parseMarkdownTokens(markdown))
  );

const isWhitespace = (inline: JSONContent, keptComments: ReadonlySet<string>) =>
  inline.type === "text" &&
  !inline.text?.trim() &&
  !(inline.marks ?? []).some(
    (mark) =>
      mark.type === COMMENT_MARK_NAME && keptComments.has(mark.attrs?.id)
  );

const trimmedStart = (inline: JSONContent): JSONContent =>
  inline.type === "text" && inline.text?.trim()
    ? { ...inline, text: inline.text.trimStart() }
    : inline;

const trimmedEnd = (inline: JSONContent): JSONContent =>
  inline.type === "text" && inline.text?.trim()
    ? { ...inline, text: inline.text.trimEnd() }
    : inline;

const withoutLineStartWhitespace = (
  inlines: JSONContent[],
  keptComments: ReadonlySet<string>
): JSONContent[] => {
  const content: JSONContent[] = [];
  let atLineStart = true;
  for (const inline of inlines) {
    if (inline.type === "hardBreak") {
      content.push(inline);
      atLineStart = true;
    } else if (!atLineStart || !isWhitespace(inline, keptComments)) {
      content.push(atLineStart ? trimmedStart(inline) : inline);
      atLineStart = false;
    }
  }
  return content;
};

const withoutLineEndWhitespace = (
  inlines: JSONContent[],
  keptComments: ReadonlySet<string>
): JSONContent[] => {
  const reversed: JSONContent[] = [];
  let atLineEnd = true;
  for (let index = inlines.length - 1; index >= 0; index -= 1) {
    const inline = inlines[index];
    if (inline.type === "hardBreak") {
      if (reversed.length > 0) {
        reversed.push(inline);
      }
      atLineEnd = true;
    } else if (!atLineEnd || !isWhitespace(inline, keptComments)) {
      reversed.push(atLineEnd ? trimmedEnd(inline) : inline);
      atLineEnd = false;
    }
  }
  return reversed.reverse();
};

const inMarkdownForm = (
  node: JSONContent,
  keptComments: ReadonlySet<string>
): JSONContent => {
  const formed = node.content
    ? {
        ...node,
        content: node.content.map((child) =>
          inMarkdownForm(child, keptComments)
        ),
      }
    : node;
  switch (formed.type) {
    case "doc":
      return withoutTrailingParagraphs(formed);
    case "paragraph":
    case "heading":
      return {
        ...formed,
        content: withoutLineEndWhitespace(
          withoutLineStartWhitespace(formed.content ?? [], keptComments),
          keptComments
        ),
      };
    case "table":
      return withColumnAlignments(formed);
    case LIST_ITEM_NODE_NAME:
      return formed.attrs?.task && !isWritableTask(formed)
        ? { ...formed, attrs: { ...formed.attrs, task: false, checked: false } }
        : formed;
    default:
      return formed;
  }
};

/**
 * @cc [owner:rfrenoy,label:product] document-markdown-form-losses
 * The Markdown form MUST differ from the document only by these losses, apart from the whitespace
 * `document-markdown-form-comment-whitespace` keeps, none of which changes the text a reader sees
 * once whitespace is collapsed:
 * - empty paragraphs ending the document;
 * - whitespace starting or ending a line of a paragraph or a heading, at the block's edges or
 *   next to a hard break, across text nodes whatever their marks, never in a code block;
 * - hard breaks ending a paragraph or a heading, so one holding only whitespace and hard breaks
 *   becomes empty;
 * - a table cell's alignment where it differs from its column's, the first aligned cell's;
 * - the task of a list item whose first paragraph is empty in the form, which becomes a plain
 *   item.
 * Encodings that keep structure, such as `#` for an empty heading, MUST stay in the writers.
 */
/**
 * @cc [owner:rfrenoy,label:product] document-markdown-form-comment-whitespace
 * Whitespace `document-markdown-form-losses` drops MUST be kept when it carries the mark of a
 * comment that marks no other text the form keeps, so the comment keeps its anchor.
 */
/**
 * @cc [owner:rfrenoy,label:product] document-markdown-form-stable
 * The Markdown form of a document's Markdown form MUST be that form.
 */
export const toMarkdownForm = (document: JSONContent): JSONContent => {
  const anchored = getMarkedCommentIds(inMarkdownForm(document, new Set()));
  const keptComments = new Set(
    [...getMarkedCommentIds(document)].filter((id) => !anchored.has(id))
  );
  return inMarkdownForm(document, keptComments);
};

const comparable = (document: JSONContent) =>
  normalizeTextNodes(documentSchema.nodeFromJSON(document));

/** A node's type, with its marks' names when it has any: no text. */
const describeNode = (node: Node | null): string => {
  if (!node) {
    return "end";
  }
  if (!node.marks.length) {
    return node.type.name;
  }
  const marks = node.marks.map((mark) => mark.type.name).join(",");
  return `${node.type.name}[${marks}]`;
};

/** The node types from the document down to `position`, then the node there: no text. */
const describePosition = (document: Node, position: number): string => {
  const resolved = document.resolve(position);
  const path = Array.from(
    { length: resolved.depth },
    (_, depth) => resolved.node(depth + 1).type.name
  );
  return [...path, describeNode(resolved.nodeAfter)].join(" > ");
};

/**
 * Where `reopened` differs from the Markdown form `document` was written in, or null when they
 * are the same. Compares in the editor's form, comment marks included, as the user would reopen it.
 */
const roundTripDifference = ({
  document,
  reopened,
}: {
  document: JSONContent;
  reopened: JSONContent;
}): string | null => {
  const written = comparable(document);
  const read = comparable(reopened);
  const position = written.content.findDiffStart(read.content);
  return position === null
    ? null
    : `${describePosition(written, position)}, which reads back as ${describePosition(read, position)}`;
};

// Comment marks share one rank, so their order on a text node only records which was added
// first: a comment added around an existing one comes after it, but reopens before it.
const sortCommentMarks = (marks: readonly Mark[]): readonly Mark[] => {
  const comments = marks
    .filter((mark) => mark.type.name === COMMENT_MARK_NAME)
    .sort((a, b) => (String(a.attrs.id) < String(b.attrs.id) ? -1 : 1));
  let next = 0;
  return marks.map((mark) =>
    mark.type.name === COMMENT_MARK_NAME ? comments[next++] : mark
  );
};

/**
 * @cc [owner:tdraier,label:product] document-comment-mark-order
 * Documents that differ only in the order of comment marks on a text MUST compare equal, so a
 * comment added around or over an existing one saves.
 */
export const normalizeTextNodes = (node: Node): Node => {
  if (node.isText) {
    return node.mark(sortCommentMarks(node.marks));
  }
  const children: Node[] = [];
  node.forEach((child) => children.push(normalizeTextNodes(child)));
  return node.copy(Fragment.fromArray(children));
};

const isValidDocument = (document: JSONContent) => {
  try {
    // TipTap opens an empty body as one empty paragraph.
    documentSchema
      .nodeFromJSON(
        document.content?.length
          ? document
          : { ...document, content: [{ type: "paragraph" }] }
      )
      .check();
    return true;
  } catch {
    return false;
  }
};

/**
 * @cc [owner:flvndvd,label:product] document-source-preservation
 * Markdown containing unsupported tokens or formatting that cannot survive serialization
 * MUST be rejected before editing. Callers MUST retain the original source for display.
 */
/**
 * @cc [owner:tdraier,label:product] document-schema-valid
 * Parsed Markdown that does not satisfy the editor schema MUST be rejected, never handed to the
 * editor, so a file the editor misreads still shows as source.
 */
/**
 * @cc [owner:PopDaph,label:product] document-refusal-reason
 * A refusal MUST name the first element the editor cannot keep, and MUST NOT quote the content.
 */
export const parseDocumentContent = (
  content: string
): Result<MarkedDocument, string> => {
  const lexed = lexMarkdown(content);
  const unsupported = findUnsupportedToken(lexed.tokens);
  if (unsupported) {
    const element = unsupportedElement(unsupported);
    const name = element
      ? UNSUPPORTED_ELEMENT_NAMES[element]
      : `"${unsupported.type}" Markdown`;
    return new Err(
      `The Markdown uses formatting the editor cannot keep: ${name}.`
    );
  }

  let parsed: JSONContent;
  try {
    parsed = parseMarkdown(lexed);
  } catch {
    return new Err("The Markdown could not be parsed.");
  }
  if (!readsTaskItems(lexed, parsed)) {
    return new Err("The Markdown's task lists would not read back the same.");
  }

  const marked = anchorsToMarks(parsed, documentSchema);
  if (marked.isErr()) {
    return marked;
  }
  if (!isValidDocument(marked.value.document)) {
    return new Err(
      "The Markdown does not fit the editor's document structure."
    );
  }

  // Opening is only safe when saving the untouched document goes through.
  if (
    serializeDocumentMarkdown(
      marked.value.document,
      marked.value.anchorOrder
    ).isErr()
  ) {
    return new Err("The Markdown would not read back the same after editing.");
  }

  return marked;
};

/**
 * The blocks of pasted `markdown` as a file with that body opens, without comment marks, or null
 * when it would not open.
 */
export const parseMarkdownBlocks = (markdown: string): JSONContent[] | null => {
  const parsed = parseDocumentContent(markdown);
  return parsed.isOk()
    ? (withoutOrphanCommentMarks(parsed.value.document, new Set()).content ??
        [])
    : null;
};

const serializeReadingBack = (
  content: JSONContent,
  anchorOrder: string[],
  formatting: AnchorFormatting
): Result<string, string> => {
  // Serializing or re-reading an unknown node throws; either way the document is not writable.
  try {
    const markdown = serializeWithAnchors(content, anchorOrder, formatting);
    if (markdown.isErr()) {
      return markdown;
    }
    const lexed = lexMarkdown(markdown.value);
    const unsupported = findUnsupportedToken(lexed.tokens);
    if (unsupported) {
      return new Err(
        `The document would be written as ${unsupported.type} Markdown, which the editor cannot read.`
      );
    }
    // An anchor refusal names its comment: it is logged as is, not as a difference.
    const reopened = anchorsToMarks(parseMarkdown(lexed), documentSchema);
    if (reopened.isErr()) {
      return reopened;
    }
    const difference = roundTripDifference({
      document: content,
      reopened: reopened.value.document,
    });
    return difference === null
      ? markdown
      : new Err(
          `The document would not read back the same as Markdown, from ${difference}.`
        );
  } catch {
    return new Err("The document could not be written as Markdown.");
  }
};

/**
 * @cc [owner:flvndvd,label:product] document-markdown-save
 * Markdown output MUST be written from the document's `toMarkdownForm`, and MUST reopen as that
 * form with the same content and formatting. A failed conversion MUST NOT reach persistence or
 * acknowledge the draft.
 */
/**
 * @cc [owner:tdraier,label:product] document-anchors-in-formatting
 * Comment anchors MUST be written inside the formatting of the text they comment, as agents
 * anchoring a quote write them, whenever that Markdown reads back the same, and with the
 * formatting shared by both sides otherwise.
 */
export const serializeDocumentMarkdown = (
  document: JSONContent,
  anchorOrder: string[] = []
): Result<string, string> => {
  const content = toMarkdownForm(document);
  const commented = serializeReadingBack(content, anchorOrder, "commented");
  return commented.isOk()
    ? commented
    : serializeReadingBack(content, anchorOrder, "shared");
};

// Resolves no image source: images render as their alt text, and Frames as their path.
export const serializeDocumentHtml = (document: JSONContent): string =>
  renderToHTMLString({ extensions: documentExtensions, content: document });

const INLINE_NODE_TYPES = new Set(["text", "hardBreak"]);

const SINGLE_PARAGRAPH_MESSAGE =
  "This suggestion is not text for a single paragraph.";

/**
 * @cc [owner:tdraier,label:product] document-inline-markdown
 * Inline content MUST be read only from Markdown that is one paragraph of text and hard
 * breaks, empty Markdown reading as no content, and refused otherwise. Markdown written for
 * inline content MUST read back as the same content, or be refused.
 */
export const parseInlineMarkdown = (
  markdown: string
): Result<JSONContent[], string> => {
  if (markdown.trim() === "") {
    return new Ok([]);
  }
  const lexed = lexMarkdown(markdown);
  if (!hasSupportedMarkdown(lexed)) {
    return new Err("The suggestion uses formatting the editor cannot keep.");
  }
  let parsed: JSONContent;
  try {
    parsed = parseMarkdown(lexed);
  } catch {
    return new Err("The suggestion could not be read.");
  }
  const blocks = withoutTrailingParagraphs(parsed).content ?? [];
  if (blocks.length !== 1 || blocks[0].type !== "paragraph") {
    return new Err(SINGLE_PARAGRAPH_MESSAGE);
  }
  const content = blocks[0].content ?? [];
  if (content.some((node) => !INLINE_NODE_TYPES.has(node.type ?? ""))) {
    return new Err(SINGLE_PARAGRAPH_MESSAGE);
  }
  return new Ok(content);
};

const paragraphOf = (content: JSONContent[]): Node =>
  normalizeTextNodes(
    documentSchema.nodeFromJSON({ type: "paragraph", content })
  );

export const serializeInlineMarkdown = (
  content: JSONContent[]
): Result<string, string> => {
  let markdown: string;
  try {
    markdown = documentMarkdown
      .serialize({ type: "doc", content: [{ type: "paragraph", content }] })
      .replace(/\n+$/, "");
  } catch {
    return new Err("This text cannot be written as Markdown.");
  }
  const reread = parseInlineMarkdown(markdown);
  return reread.isOk() && paragraphOf(reread.value).eq(paragraphOf(content))
    ? new Ok(markdown)
    : new Err("This text would not read back the same as Markdown.");
};
